<script setup lang="ts">
import CopilotComposer from "~/components/Copilot/composer/CopilotComposer.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";

withDefaults(
  defineProps<{
    title: string;
    modelValue: string;
    icon?: string;
    maxWidth?: string;
    disclaimer?: string | null;
    inputPlaceholder?: string;
    inputDisabled?: boolean;
    busy?: boolean;
    canAttach?: boolean;
    accept?: string;
    /** Staged file waiting to be sent, shown as a removable chip on the composer. */
    attachmentName?: string | null;
    /**
     * assistant-over-modals: float above full-screen modals (z 500) instead of under them, so the
     * panel stays usable over a session detail. Off by default — every other caller is unchanged.
     */
    aboveModals?: boolean;
  }>(),
  {
    icon: "ai",
    maxWidth: "560px",
    disclaimer: null,
    inputPlaceholder: "Écrivez un message...",
    inputDisabled: false,
    busy: false,
    canAttach: false,
    accept: "application/pdf",
    attachmentName: null,
    aboveModals: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: string];
  close: [];
  send: [];
  stop: [];
  file: [file: File];
  "remove-attachment": [];
}>();

const bodyRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);
const inputRef = ref<InstanceType<typeof CopilotComposer> | null>(null);
let resizeObserver: ResizeObserver | null = null;

/** Below this distance from the bottom the user is considered to be following the stream. */
const STICK_THRESHOLD_PX = 80;
const isStuckToBottom = ref(true);

function scrollToBottom() {
  const body = bodyRef.value;
  if (body) body.scrollTop = body.scrollHeight;
}

/** Follows the last message while streaming, unless the user has scrolled up to read back. */
function scrollToBottomIfStuck() {
  if (isStuckToBottom.value) scrollToBottom();
}

function onBodyScroll() {
  const body = bodyRef.value;
  if (!body) return;

  isStuckToBottom.value = body.scrollHeight - body.scrollTop - body.clientHeight <= STICK_THRESHOLD_PX;
}

function focusInput() {
  inputRef.value?.focus();
}

function keyEscPress(event: KeyboardEvent) {
  if (event.key === "Escape") emit("close");
}

onMounted(() => {
  document.addEventListener("keyup", keyEscPress);
  focusInput();

  if (contentRef.value) {
    resizeObserver = new ResizeObserver(() => scrollToBottomIfStuck());
    resizeObserver.observe(contentRef.value);
  }
});

onUnmounted(() => {
  document.removeEventListener("keyup", keyEscPress);
  resizeObserver?.disconnect();
});

defineExpose({ scrollToBottom, focusInput });
</script>

<template>
  <!--
    assistant-over-modals: a z-index only competes inside its own stacking context, so when the panel
    has to sit over a full-screen modal it is teleported out to `body` first — the same escape hatch
    Tooltip (`appendToBody`), PanelActionsButton and the upload croppers already use. Teleport moves
    the DOM without touching component state, so the conversation survives the move both ways.
  -->
  <Teleport to="body" :disabled="!aboveModals">
    <!--
      A card inset from the header and the viewport edges rather than a panel welded to them: the
      page behind keeps its own chrome, and the panel reads as one closed surface wherever it opens.
      Still `fixed` on the right, over the page: nothing behind it moves, and it dims nothing — so
      it carries the floating trigger's own `stroke-color` border and `--shadow-medium`, the pair
      that lifts a white surface off a white page. The `shadow:` hint is load-bearing: without it
      Tailwind reads the arbitrary value as a shadow *colour* and emits no box-shadow at all.
      Starts below the header: it carries the triggers, so it must stay clickable.
    -->
    <aside
      class="fixed bottom-2 right-2 flex w-[calc(100%-16px)] flex-col overflow-hidden rounded-[14px] border border-stroke-color bg-light-color shadow-[shadow:var(--shadow-medium)]"
      :class="aboveModals ? 'z-[600]' : 'z-[150]'"
      :style="{ maxWidth, top: 'calc(var(--header_height) + 8px)' }"
    >
      <!-- Kept to a single 42px row, so the header gives the conversation back the space it took. -->
      <header class="flex h-[42px] shrink-0 items-center justify-between gap-2 px-5">
        <div class="flex min-w-0 items-center gap-2">
          <SvgIcon :name="icon" class="h-[28px] w-[28px] shrink-0 text-primary-blue-color" />
          <span class="heading_4_bold truncate leading-[22px] text-primary-color">
            <slot name="title">{{ title }}</slot>
          </span>
        </div>
        <!-- The cross the modals use: a bare 24px glyph, no chip. Kept a button for the keyboard. -->
        <button
          type="button"
          class="shrink-0 cursor-pointer text-secondary-color"
          aria-label="Fermer"
          @click="emit('close')"
        >
          <SvgIcon name="close" class="h-[24px] w-[24px]" />
        </button>
      </header>

      <!-- #body replaces the whole scrollable container, for libraries that handle scrolling themselves -->
      <slot name="body">
        <div ref="bodyRef" class="flex-1 overflow-y-auto px-6 py-4" @scroll.passive="onBodyScroll">
          <div ref="contentRef">
            <slot></slot>
          </div>
        </div>
      </slot>

      <footer class="flex shrink-0 flex-col gap-[10px] border-t border-stroke-color px-5 pb-3.5 pt-3">
        <slot name="footer-top"></slot>
        <slot name="input">
          <CopilotComposer
            ref="inputRef"
            :model-value="modelValue"
            :placeholder="inputPlaceholder"
            :disabled="inputDisabled"
            :busy="busy"
            :can-attach="canAttach"
            :accept="accept"
            :attachment-name="attachmentName"
            @update:model-value="emit('update:modelValue', $event)"
            @send="emit('send')"
            @stop="emit('stop')"
            @file="emit('file', $event)"
            @remove-attachment="emit('remove-attachment')"
          />
        </slot>
        <span v-if="disclaimer" class="small_regular text-center leading-4 text-secondary-color">{{ disclaimer }}</span>
      </footer>
    </aside>
  </Teleport>
</template>
